import React, { Component, type KeyboardEvent, type MouseEvent } from 'react';
import { styled, css } from '@compiled/react';
import keycode from 'keycode';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

type Props = {
	isExpanded: boolean;
	label: string;
	onClick: () => void;
	onKeyDown: () => void;
};

// eslint-disable-next-line jira/react/no-class-components
class ListItemChevron extends Component<Props> {
	onKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
		if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
			event.preventDefault();
			event.stopPropagation();

			this.props.onKeyDown();
		}
	};

	onClick = (event: MouseEvent<HTMLSpanElement>) => {
		event.stopPropagation();

		this.props.onClick();
	};

	render() {
		const { isExpanded, label } = this.props;

		/**
		 * Wrapping Atlaskit icon and pass the colour value in the parent props, so we can replace those values with actual colour value.
		 *
		 * Atlaskit icon component uses primaryColor and secondaryColor to set 'currentColor' and 'inherit' in fill attribute.
		 * html2canvas library does not properly render currentColor and inherit values to colors.
		 */
		// RLSS-2812 replace color.icon.subtle into color.icon.accent.gray
		return (
			<ButtonWrapper
				role="button"
				// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'.
				tabIndex="0"
				aria-expanded={isExpanded}
				primarycolor={token('color.icon.subtle', colors.N500)}
				onKeyDown={this.onKeyDown}
				onClick={this.onClick}
				isExpanded={isExpanded}
			>
				<ChevronRightIcon primaryColor={token('color.icon.subtle', colors.N500)} label={label} />
			</ButtonWrapper>
		);
	}
}

interface ButtonWrapperProps {
	isExpanded: boolean;
}

const boxShadow = `0 0 0 2px ${token('elevation.shadow.raised', colors.B100)}`;

/* A focus border that appears only when focus is attained via the keyboard.
 * Implemented via the .focus-visible class that comes from the 'focus-visible' polyfill.
 * Native outline is always disabled as as we either have:
 * 1) A mouse user who does not need the focus style
 * 2) A keyboard user, who will see our custom focus border
 */
const focusBorderStyles = css({
	outline: 'none',
	transition: 'box-shadow 0.2s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:focus.focus-visible': {
		boxShadow,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span<ButtonWrapperProps>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		transform: ({ isExpanded }: ButtonWrapperProps) =>
			isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
		transformOrigin: '50% 50%',
		display: 'flex',
		flex: '0 0 auto',
		cursor: 'pointer',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	focusBorderStyles,
);

export default ListItemChevron;
